import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { FontAttributes, TextAlignValue } from 'js/types';

export interface TextStyle {
  id: string;
  name: string;
  isDefault: boolean;
  font: FontAttributes;
  colorHex: string;
  lineSpacing: number;
  fontSize: number;
  bold: boolean;
  italic: boolean;
  align: TextAlignValue;
}

export interface BrandKitFontsState {
  lastSynced: number | null;
  useBrandKitFonts: boolean;
  styles: TextStyle[];
  fontUploadStatus: 'idle' | 'uploading' | 'success' | 'error';
  fontUploadError: string | null;
}

const DEFAULT_STYLE: TextStyle = {
  id: 'body',
  name: 'Body',
  isDefault: true,
  font: { label: 'Open Sans', value: 'Open Sans' },
  colorHex: '#123049',
  lineSpacing: 1.4,
  fontSize: 30,
  bold: false,
  italic: false,
  align: 'left'
};

const initialState: BrandKitFontsState = {
  lastSynced: null,
  useBrandKitFonts: false,
  styles: [DEFAULT_STYLE],
  fontUploadStatus: 'idle',
  fontUploadError: null
};

const brandKitFontsSlice = createSlice({
  name: 'brandKitFonts',
  initialState,
  reducers: {
    loadFontsSuccess: (state, action: PayloadAction<{ styles: TextStyle[]; useBrandKitFonts: boolean }>) => {
      state.styles = action.payload.styles;
      state.useBrandKitFonts = action.payload.useBrandKitFonts;
    },
    setUseBrandKitFonts: (state, action: PayloadAction<boolean>) => {
      state.useBrandKitFonts = action.payload;
    },
    addStyle: (state, action: PayloadAction<TextStyle>) => {
      state.styles.push(action.payload);
    },
    removeStyle: (state, action: PayloadAction<string>) => {
      const index = state.styles.findIndex(s => s.id === action.payload);
      if (index === -1 || state.styles.length <= 1) return;
      const wasDefault = state.styles[index].isDefault;
      state.styles.splice(index, 1);
      if (wasDefault) {
        state.styles[0].isDefault = true;
      }
    },
    updateStyle: (state, action: PayloadAction<{ id: string; patch: Partial<TextStyle> }>) => {
      const style = state.styles.find(s => s.id === action.payload.id);
      if (style) Object.assign(style, action.payload.patch);
    },
    setDefaultStyle: (state, action: PayloadAction<string>) => {
      for (const style of state.styles) {
        style.isDefault = style.id === action.payload;
      }
    },
    setLastSynced: (state, action: PayloadAction<number>) => {
      state.lastSynced = action.payload;
    },
    brandKitFontUploadStarted: state => {
      state.fontUploadStatus = 'uploading';
      state.fontUploadError = null;
    },
    brandKitFontUploadSuccess: (state, _action: PayloadAction<string>) => {
      state.fontUploadStatus = 'success';
    },
    brandKitFontUploadError: (state, action: PayloadAction<string>) => {
      state.fontUploadStatus = 'error';
      state.fontUploadError = action.payload;
    },
    brandKitFontUploadReset: state => {
      state.fontUploadStatus = 'idle';
      state.fontUploadError = null;
    }
  }
});

export const {
  loadFontsSuccess,
  setUseBrandKitFonts,
  addStyle,
  removeStyle,
  updateStyle,
  setDefaultStyle,
  setLastSynced,
  brandKitFontUploadStarted,
  brandKitFontUploadSuccess,
  brandKitFontUploadError,
  brandKitFontUploadReset
} = brandKitFontsSlice.actions;

export default brandKitFontsSlice.reducer;
